import { isRoleAllowedOnPortal, type PortalType } from '@mdvip/shared/auth/portalAccess';
import { useAbility, useAuthStore } from '@mdvip/shared/stores/authStore';
import type { Action, Subject } from '@mdvip/shared/types/ability';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

interface ProtectedRouteProps {
  children: ReactNode;
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * Optional ability gate. When provided, the route requires the user's ability
   * to allow `action` on `subject`. Failed checks redirect to `/` (not a 403
   * page) to avoid accidental lockouts during initial rollout.
   */
  can?: [Action, Subject];
  /**
   * Which portal this app is. When provided, an authenticated user
   * whose role doesn't match the portal is shown a "wrong portal" notice instead
   * of the route — the already-signed-in case the backend can't catch at login.
   */
  portalType?: PortalType;
}

export function ProtectedRoute({ children, isAuthenticated, isLoading, can, portalType }: ProtectedRouteProps) {
  const { t } = useTranslation('shared');
  const ability = useAbility();
  // Derive the portal mismatch from raw inputs (the session user + this app's
  // portal) rather than storing it — mirrors how isMember/isAuthenticated are
  // derived in useAuth. portalType is undefined in tests, which disables the gate.
  const user = useAuthStore((s) => s.user);
  const portalMismatch = !!portalType && !!user && !isRoleAllowedOnPortal(user.userType, portalType);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
          <p className="mt-4 text-lg text-muted-foreground">{t('auth.loading')}</p>
        </div>
      </div>
    );
  }

  // An already-signed-in user whose role doesn't match this portal
  // (the auth cookie is shared across portals via the common API host). Show a
  // notice instead of bouncing to /login — re-login would silently loop.
  if (portalMismatch) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="text-center">
          <p className="text-sm text-destructive">{t('auth.wrongPortal')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (can && !(ability?.can(...can) ?? false)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
